import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useDeleteQuiz } from '@/hooks/useQuizzes';
import type { Quiz } from '@/types/quiz';

interface DeleteQuizDialogProps {
  open: boolean;
  onClose: () => void;
  quiz: Quiz | null;
}

export function DeleteQuizDialog({ open, onClose, quiz }: DeleteQuizDialogProps) {
  const deleteMutation = useDeleteQuiz();
  const [error, setError] = useState('');

  if (!open || !quiz) return null;

  async function handleDelete() {
    if (!quiz) return;
    setError('');

    try {
      await deleteMutation.mutateAsync(quiz.quizId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        role="button"
        tabIndex={-1}
        aria-label="Dialog schließen"
      />
      <div className="relative z-50 w-full max-w-md rounded-lg border bg-background p-6 shadow-lg mx-4">
        <h2 className="text-lg font-semibold">Quiz löschen</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Möchten Sie das Quiz <span className="font-medium text-foreground">&bdquo;{quiz.title}&ldquo;</span> wirklich löschen?
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Das Quiz wird archiviert und ist nicht mehr für Endkunden sichtbar.
        </p>

        {error && (
          <p className="mt-3 text-sm text-destructive">{error}</p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending}>
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Wird gelöscht...' : 'Löschen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
